import { useEffect, useState } from 'react';

interface SafeAreaInsets {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

export const useSafeArea = () => {
  const [safeAreaInsets, setSafeAreaInsets] = useState<SafeAreaInsets>({
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
  });

  useEffect(() => {
    // 监听安全区域更新事件
    const handleSafeAreaUpdate = (event: CustomEvent<SafeAreaInsets>) => {
      setSafeAreaInsets(event.detail);
      console.log('Safe area insets updated via event:', event.detail);
    };

    // 从CSS变量读取安全区域信息的函数
    const readSafeAreaFromCSS = () => {
      const computedStyle = getComputedStyle(document.documentElement);
      const top = parseFloat(computedStyle.getPropertyValue('--safe-area-inset-top')) || 0;
      const bottom = parseFloat(computedStyle.getPropertyValue('--safe-area-inset-bottom')) || 0;
      const left = parseFloat(computedStyle.getPropertyValue('--safe-area-inset-left')) || 0;
      const right = parseFloat(computedStyle.getPropertyValue('--safe-area-inset-right')) || 0;
      
      const cssInsets = { top, bottom, left, right };
      return cssInsets;
    };

    // 添加事件监听器
    document.addEventListener('safeAreaInsetsUpdated', handleSafeAreaUpdate as EventListener);

    // 初始化时请求安全区域信息
    if (window.webkit?.messageHandlers?.safeAreaRequest) {
      console.log('Requesting safe area insets from native...');
      window.webkit.messageHandlers.safeAreaRequest.postMessage('request');
    } else {
      console.log('Native message handler not available, reading from CSS...');
      // 如果没有原生消息处理器，尝试从CSS变量读取
      const cssInsets = readSafeAreaFromCSS();
      if (cssInsets.top > 0 || cssInsets.bottom > 0 || cssInsets.left > 0 || cssInsets.right > 0) {
        setSafeAreaInsets(cssInsets);
      }
    }

    // 定期检查CSS变量（作为fallback）
    const intervalId = setInterval(() => {
      const cssInsets = readSafeAreaFromCSS();
      if (cssInsets.top > 0 || cssInsets.bottom > 0 || cssInsets.left > 0 || cssInsets.right > 0) {
        setSafeAreaInsets(prev => {
          // 只有当值发生变化时才更新
          if (JSON.stringify(prev) !== JSON.stringify(cssInsets)) {
            console.log('Safe area insets updated from CSS polling:', cssInsets);
            return cssInsets;
          }
          return prev;
        });
      }
    }, 1000); // 每秒检查一次

    // 清理函数
    return () => {
      document.removeEventListener('safeAreaInsetsUpdated', handleSafeAreaUpdate as EventListener);
      clearInterval(intervalId);
    };
  }, []);

  return safeAreaInsets;
};

// 扩展 Window 接口以支持 webkit 消息处理器
declare global {
  interface Window {
    webkit?: {
      messageHandlers?: {
        safeAreaRequest?: {
          postMessage: (message: string) => void;
        };
      };
    };
  }
}